/**
 * @dnd-kit runs drag-end handling outside the field list React subtree. This module keeps a ref
 * (`dndPageFieldMutatorsRef`) mapping page index → insert/move + current `fields` snapshot so
 * `handleFormEditorDragEnd` can mutate `pages[i].fields` without a nested `useFieldArray` per page.
 */
import type {MutableRefObject} from 'react';
import {useEffect} from 'react';
// @ts-expect-error
import type {UseFormGetValues} from 'react-hook-form';

import type {PageFieldArrayOps} from '../drag-and-drop/types';
import type {FormFieldArrayRow, FormPageWithRowKey, MultiPageForm} from '../types';

type UpdatePageRow = (index: number, value: FormPageWithRowKey) => void;

function createPageFieldArrayOps(
    pageIndex: number,
    getValues: UseFormGetValues<MultiPageForm>,
    updatePage: UpdatePageRow
): PageFieldArrayOps {
    const pages = getValues('pages') ?? [];
    const page = pages[pageIndex];
    const fieldsForOps = page?.fields ?? [];

    return {
        fields: fieldsForOps,
        insert: (insertIndex, value) => {
            const currentPages = getValues('pages') ?? [];
            const p = currentPages[pageIndex];
            if (!p) {
                return;
            }
            const next = [...(p.fields ?? [])];
            const row = {...value, key: (value as {id: string}).id};
            next.splice(insertIndex, 0, row as FormFieldArrayRow);
            updatePage(pageIndex, {...p, fields: next});
        },
        move: (from, to) => {
            const currentPages = getValues('pages') ?? [];
            const p = currentPages[pageIndex];
            if (!p) {
                return;
            }
            const next = [...(p.fields ?? [])];
            const [removed] = next.splice(from, 1);
            next.splice(to, 0, removed);
            updatePage(pageIndex, {...p, fields: next});
        },
    };
}

export function useSyncPageFieldOpsRef(options: {
    dndPageFieldMutatorsRef: MutableRefObject<Map<number, PageFieldArrayOps>>;
    getValues: UseFormGetValues<MultiPageForm>;
    updatePage: UpdatePageRow;
    watchedPages: unknown;
}): void {
    const {dndPageFieldMutatorsRef, getValues, updatePage, watchedPages} = options;

    useEffect(() => {
        const map = dndPageFieldMutatorsRef.current;
        map.clear();
        const pages = getValues('pages') ?? [];
        for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
            if (!pages[pageIndex]) {
                continue;
            }
            map.set(pageIndex, createPageFieldArrayOps(pageIndex, getValues, updatePage));
        }
        return () => {
            map.clear();
        };
    }, [watchedPages, getValues, dndPageFieldMutatorsRef, updatePage]);
}
